import React from "react";
import FilterField from "../fields/FilterField";
import FilterMultiSelect from "../fields/FilterMultiSelect";
import FilterSelect from "../fields/FilterSelect";
import { CompactDateInput } from "./jobsFilterUtils";
import formatGroupedValues from "../utils/formatGroupedValues";
import { dedupeClientFilterOptions } from "../utils/dedupeClientFilterOptions";

export const SCHEDULE_DATE_RANGE_OPTIONS = [
  { value: "1 Day", label: "1 Day" },
  { value: "7 Days", label: "7 Days" },
  { value: "14 Days", label: "14 Days" },
  { value: "30 Days", label: "30 Days" },
];

function FieldWrapper({ fieldId, label, focusedFieldId, onFocusClear, children }) {
  return (
    <FilterField
      id={fieldId}
      label={label}
      focusedFieldId={focusedFieldId}
      onFocusClear={onFocusClear}
    >
      {children}
    </FilterField>
  );
}

export function ScheduleFilterFields({
  focusedFieldId,
  onFocusClear,
  selectedDivisions,
  setSelectedDivisions,
  selectedAssignmentType,
  setSelectedAssignmentType,
  selectedAgencies,
  setSelectedAgencies,
  selectedEmploymentOffices,
  setSelectedEmploymentOffices,
  selectedClients,
  setSelectedClients,
  selectedStaffIds,
  setSelectedStaffIds,
  selectedPositions,
  setSelectedPositions,
  selectedDate,
  setSelectedDate,
  selectedDateRange,
  setSelectedDateRange,
  selectedShiftStatuses,
  setSelectedShiftStatuses,
  
  divisions = [],
  assignmentTypes = [],
  agencies = [],
  employmentOffices = [],
  clients = [],
  staff = [],
  positions = [],
  shiftStatuses = [],
  
  staffFilterLoading = false,
  onStaffQueryChange,
}) {
  const divisionOptions = divisions.map((d) => {
    const name = String(d?.name ?? "").trim();
    return { value: name, label: name };
  });

  const assignmentTypeOptions = [
    { value: "", label: "All" },
    ...assignmentTypes.map((t) => ({
      value: String(t.name),
      label: String(t.name),
    })),
  ];

  const agencyOptions = agencies.map((a) => ({
    value: String(a.name),
    label: String(a.name),
  }));

  const officeOptions = employmentOffices.map((o) => ({
    value: String(o.name),
    label: String(o.name),
  }));

  const clientOptions = dedupeClientFilterOptions(
    clients.map((c) => ({
      value: String(c.name),
      label: String(c.name),
    })),
  );

  const staffOptions = staff.map((s) => ({
    value: String(s.id),
    label: String(s.name),
  }));

  const positionOptions = positions.map((p) => ({
    value: String(p.name),
    label: String(p.name),
  }));

  const statusOptions = shiftStatuses.map((s) => ({
    value: String(s.name),
    label: String(s.name),
  }));

  return (
    <>
      <FieldWrapper fieldId="division" label="Division" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="schedule-division"
          options={divisionOptions}
          value={selectedDivisions}
          onChange={setSelectedDivisions}
          placeholder="Select Division"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="assignmentType" label="Assignment Type" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSelect
          id="schedule-assignment-type"
          value={selectedAssignmentType}
          onChange={setSelectedAssignmentType}
          options={assignmentTypeOptions}
          placeholder={null}
        />
      </FieldWrapper>

      <FieldWrapper fieldId="agency" label="Agency" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="schedule-agency"
          options={agencyOptions}
          value={selectedAgencies}
          onChange={setSelectedAgencies}
          placeholder="Select Agency"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="employmentOffice" label="Employment Office" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="schedule-employment-office"
          options={officeOptions}
          value={selectedEmploymentOffices}
          onChange={setSelectedEmploymentOffices}
          placeholder="Select Employment Office"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="client" label="Client" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="schedule-client"
          options={clientOptions}
          value={selectedClients}
          onChange={setSelectedClients}
          placeholder="Select Client"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="staff" label="Staff" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="schedule-staff"
          options={staffOptions}
          value={selectedStaffIds.map(String)}
          onChange={(val) => setSelectedStaffIds(val.map(Number))}
          placeholder="Select Staff"
          loading={staffFilterLoading}
          onQueryChange={onStaffQueryChange}
        />
      </FieldWrapper>

      <FieldWrapper fieldId="position" label="Position" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="schedule-position"
          options={positionOptions}
          value={selectedPositions}
          onChange={setSelectedPositions}
          placeholder="Select Position"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="date" label="Date" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <CompactDateInput
          id="schedule-date"
          value={selectedDate}
          onChange={setSelectedDate}
        />
      </FieldWrapper>

      <FieldWrapper fieldId="dateRange" label="Date Range" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSelect
          id="schedule-date-range"
          value={selectedDateRange}
          onChange={setSelectedDateRange}
          options={SCHEDULE_DATE_RANGE_OPTIONS}
          placeholder={null}
        />
      </FieldWrapper>

      <FieldWrapper fieldId="shiftStatus" label="Shift Status" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="schedule-shift-status"
          options={statusOptions}
          value={selectedShiftStatuses}
          onChange={setSelectedShiftStatuses}
          placeholder="Select shift statuses..."
        />
      </FieldWrapper>
    </>
  );
}

export function buildScheduleFilterChips({
  selectedDivisions,
  selectedAssignmentType,
  selectedAgencies,
  selectedEmploymentOffices,
  selectedClients,
  selectedStaffIds,
  selectedPositions,
  selectedDate,
  selectedDateRange,
  selectedShiftStatuses,
  staff = [],
}) {
  const chips = [];

  if (selectedDivisions.length > 0) {
    chips.push({
      id: "division",
      key: "division",
      fieldId: "division",
      label: `Divisions: ${formatGroupedValues(selectedDivisions, (v) => v, 3)}`,
    });
  }

  if (selectedAssignmentType) {
    chips.push({
      id: "assignmentType",
      key: "assignmentType",
      fieldId: "assignmentType",
      label: `Assignment: ${selectedAssignmentType}`,
    });
  }

  if (selectedAgencies.length > 0) {
    chips.push({
      id: "agency",
      key: "agency",
      fieldId: "agency",
      label: `Agencies: ${formatGroupedValues(selectedAgencies, (v) => v, 3)}`,
    });
  }

  if (selectedEmploymentOffices.length > 0) {
    chips.push({
      id: "employmentOffice",
      key: "employmentOffice",
      fieldId: "employmentOffice",
      label: `Offices: ${formatGroupedValues(selectedEmploymentOffices, (v) => v, 3)}`,
    });
  }

  if (selectedClients.length > 0) {
    chips.push({
      id: "client",
      key: "client",
      fieldId: "client",
      label: `Clients: ${formatGroupedValues(selectedClients, (v) => v, 3)}`,
    });
  }

  if (selectedStaffIds.length > 0) {
    const labelForStaffId = (id) => {
      const match = staff.find((s) => String(s.id) === String(id));
      return match?.name ? String(match.name).trim() : `#${id}`;
    };
    chips.push({
      id: "staff",
      key: "staff",
      fieldId: "staff",
      label: `Staff: ${formatGroupedValues(selectedStaffIds, labelForStaffId, 3)}`,
    });
  }

  if (selectedPositions.length > 0) {
    chips.push({
      id: "position",
      key: "position",
      fieldId: "position",
      label: `Positions: ${formatGroupedValues(selectedPositions, (v) => v, 3)}`,
    });
  }

  // Date and Range chips are non-removable (removable: false)
  if (selectedDate) {
    chips.push({
      id: "date",
      key: "date",
      fieldId: "date",
      label: `Date: ${selectedDate}`,
      removable: false,
    });
  }

  if (selectedDateRange) {
    chips.push({
      id: "dateRange",
      key: "dateRange",
      fieldId: "dateRange",
      label: `Range: ${selectedDateRange}`,
      removable: false,
    });
  }

  if (selectedShiftStatuses.length > 0) {
    chips.push({
      id: "shiftStatus",
      key: "shiftStatus",
      fieldId: "shiftStatus",
      label: `Status: ${formatGroupedValues(selectedShiftStatuses, (v) => v, 3)}`,
    });
  }

  return chips;
}
